import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { 
  BookOpen, Database, Workflow, Settings, Play, Clock,
  AlertCircle, CheckCircle2, Zap, Server, RefreshCw, Bot, Copy, Download, Check,
  FileText, Loader2
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const DocumentationPage = () => {
  const [activeSection, setActiveSection] = useState("overview");

  const sections = [
    { id: "overview", label: "Overview", icon: BookOpen },
    { id: "architecture", label: "Architecture", icon: Server },
    { id: "pipeline", label: "Pipeline Flow", icon: Workflow },
    { id: "configuration", label: "Configuration", icon: Settings },
    { id: "operations", label: "Operations Guide", icon: Play },
    { id: "data-model", label: "Data Model", icon: Database },
    { id: "ai-agent", label: "AI Agent KB", icon: Bot },
    { id: "investor", label: "Investor Materials", icon: FileText },
  ];

  return (
    <div className="space-y-6" data-testid="documentation-page">
      <div>
        <h1 className="text-2xl font-semibold">Documentation</h1>
        <p className="text-muted-foreground text-sm mt-1">
          13F Control Tower - How it works
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Navigation */}
        <Card className="bg-card border-border rounded-sm lg:col-span-1">
          <CardContent className="p-4">
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors ${
                    activeSection === section.id
                      ? "bg-primary/10 text-primary border-l-2 border-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  }`}
                >
                  <section.icon className="w-4 h-4" />
                  {section.label}
                </button>
              ))}
            </nav>
          </CardContent>
        </Card>

        {/* Content */}
        <Card className="bg-card border-border rounded-sm lg:col-span-3">
          <CardContent className="p-6">
            <ScrollArea className="h-[calc(100vh-280px)]">
              {activeSection === "overview" && <OverviewSection />}
              {activeSection === "architecture" && <ArchitectureSection />}
              {activeSection === "pipeline" && <PipelineSection />}
              {activeSection === "configuration" && <ConfigurationSection />}
              {activeSection === "operations" && <OperationsSection />}
              {activeSection === "data-model" && <DataModelSection />}
              {activeSection === "ai-agent" && <AIAgentSection />}
              {activeSection === "investor" && <InvestorMaterialsSection />}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const OverviewSection = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
      <BookOpen className="w-5 h-5 text-primary" />
      What is 13F Control Tower?
    </h2>
    
    <p className="text-muted-foreground mb-4">
      13F Control Tower is an internal data operations platform for ingesting and managing institutional 
      13F holdings data from the SEC. It transforms raw FMP (Financial Modeling Prep) API data into 
      a structured, enriched database ready for analysis.
    </p>

    <div className="grid grid-cols-2 gap-4 my-6">
      <FeatureCard
        icon={Zap}
        title="Incremental Ingestion"
        description="Only processes new or changed data, avoiding full database reloads"
      />
      <FeatureCard
        icon={RefreshCw}
        title="Resumable & Idempotent"
        description="Jobs can be paused, resumed, and re-run without creating duplicates"
      />
      <FeatureCard
        icon={Settings}
        title="Configurable"
        description="All thresholds and filters editable from the UI with versioned configs"
      />
      <FeatureCard
        icon={Clock}
        title="Real-time Monitoring"
        description="Live progress updates via Server-Sent Events (SSE)"
      />
    </div>

    <h3 className="text-lg font-semibold mt-6 mb-3">Key Concepts</h3>
    
    <div className="space-y-3">
      <ConceptItem
        term="CIK"
        definition="Central Index Key - SEC identifier for institutional investors (10-digit, zero-padded)"
      />
      <ConceptItem
        term="13F Filing"
        definition="Quarterly SEC filing required for institutional investment managers with >$100M AUM"
      />
      <ConceptItem
        term="CUSIP"
        definition="9-character identifier for securities (stocks, bonds)"
      />
      <ConceptItem
        term="Fund-Quarter Task"
        definition="The atomic unit of work - extracting holdings for one fund for one quarter"
      />
    </div>
  </div>
);

const ArchitectureSection = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
      <Server className="w-5 h-5 text-primary" />
      System Architecture
    </h2>

    <div className="bg-muted/30 p-4 rounded-sm font-mono text-sm mb-6">
      <pre>{`
┌─────────────────────────────────────────────────────────────┐
│                      Frontend (React)                        │
│  Dashboard │ Runs │ Funds │ Quarters │ Quality │ Config     │
└─────────────────────────┬───────────────────────────────────┘
                          │ REST API + SSE
┌─────────────────────────┴───────────────────────────────────┐
│                    Backend (FastAPI)                         │
│  ┌──────────┐  ┌──────────┐  ┌──────────┐  ┌──────────┐    │
│  │   Auth   │  │ Pipeline │  │  Config  │  │  Funds   │    │
│  │  Routes  │  │  Routes  │  │  Routes  │  │  Routes  │    │
│  └──────────┘  └──────────┘  └──────────┘  └──────────┘    │
│                                                              │
│  ┌──────────────────────────────────────────────────────┐   │
│  │                    Services Layer                     │   │
│  │  FMP Client │ Pipeline Service │ Job Runner │ Email  │   │
│  └──────────────────────────────────────────────────────┘   │
└─────────────────────────┬───────────────────────────────────┘
                          │
┌─────────────────────────┴───────────────────────────────────┐
│                      MongoDB                                 │
│  pipeline_configs │ pipeline_runs │ funds_master │ ...      │
└─────────────────────────────────────────────────────────────┘
                          │
                    FMP API (External)
      `}</pre>
    </div>

    <h3 className="text-lg font-semibold mt-6 mb-3">Backend Services</h3>
    
    <div className="space-y-3">
      <ServiceItem
        name="FMP Client"
        description="Handles all FMP API calls with rate limiting (300/min), retries, and caching"
      />
      <ServiceItem
        name="Pipeline Service"
        description="Orchestrates the 7-stage ingestion pipeline with progress tracking"
      />
      <ServiceItem
        name="Job Runner"
        description="Manages background job execution, pause/resume, and SSE broadcasting"
      />
      <ServiceItem
        name="Email Service"
        description="Sends progress notifications via Resend API"
      />
      <ServiceItem
        name="Scheduler Service"
        description="Manages scheduled/cron-based pipeline runs (APScheduler)"
      />
    </div>
  </div>
);

const PipelineSection = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
      <Workflow className="w-5 h-5 text-primary" />
      Pipeline Flow
    </h2>

    <p className="text-muted-foreground mb-4">
      The ingestion pipeline consists of 7 sequential stages. Each stage updates progress 
      in real-time and can be paused/resumed at any point.
    </p>

    <div className="space-y-4 my-6">
      <StageItem
        number={1}
        name="Universe Discovery"
        description="Fetches latest 13F filings from FMP, identifies unique CIKs, determines active funds based on lookback period"
        endpoint="/stable/institutional-ownership/latest"
      />
      <StageItem
        number={2}
        name="Fund Filtering"
        description="Applies performance filters (holding period, portfolio size) using holder-performance-summary API"
        endpoint="/stable/institutional-ownership/holder-performance-summary"
      />
      <StageItem
        number={3}
        name="Task Creation"
        description="Creates fund-quarter tasks for each selected fund × target quarters. Skips already successful tasks."
      />
      <StageItem
        number={4}
        name="Holdings Extraction"
        description="Fetches raw holdings data for each fund-quarter task"
        endpoint="/stable/institutional-ownership/extract"
      />
      <StageItem
        number={5}
        name="CUSIP Resolution"
        description="Resolves CUSIP codes to stock symbols and company names"
        endpoint="/stable/search-cusip"
      />
      <StageItem
        number={6}
        name="Profile Enrichment"
        description="Enriches symbols with sector and industry information"
        endpoint="/stable/profile"
      />
      <StageItem
        number={7}
        name="Finalization"
        description="Writes enriched holdings to database, updates task statuses, computes final statistics"
      />
    </div>

    <h3 className="text-lg font-semibold mt-6 mb-3">Idempotency Guarantees</h3>
    <ul className="list-disc list-inside text-muted-foreground space-y-2">
      <li>Fund-quarter tasks have unique compound index (cik, year, quarter)</li>
      <li>Holdings use upsert with compound key (cik, year, quarter, cusip_norm)</li>
      <li>Re-running same config skips already successful tasks by default</li>
      <li>Each config change creates a new version; runs store exact version used</li>
    </ul>
  </div>
);

const ConfigurationSection = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
      <Settings className="w-5 h-5 text-primary" />
      Configuration Options
    </h2>

    <h3 className="text-lg font-semibold mt-4 mb-3">Universe Discovery</h3>
    <ConfigTable
      items={[
        { param: "max_latest_pages", default: "50", description: "Max pages to fetch from latest filings API" },
        { param: "latest_limit", default: "500", description: "Results per page" },
        { param: "lookback_days", default: "120", description: "Days to consider a fund 'active'" },
      ]}
    />

    <h3 className="text-lg font-semibold mt-6 mb-3">Fund Filtering</h3>
    <ConfigTable
      items={[
        { param: "use_holder_perf_filters", default: "true", description: "Enable performance-based filtering" },
        { param: "min_avg_holding_period", default: "2.0", description: "Minimum avg holding period (years)" },
        { param: "min_portfolio_size", default: "10", description: "Minimum number of holdings" },
        { param: "max_funds", default: "100", description: "Maximum funds to process" },
      ]}
    />

    <h3 className="text-lg font-semibold mt-6 mb-3">Holdings Extraction</h3>
    <ConfigTable
      items={[
        { param: "quarters_per_fund", default: "4", description: "How many quarters of history to fetch" },
        { param: "batch_size_funds", default: "10", description: "Funds to process in parallel" },
      ]}
    />

    <h3 className="text-lg font-semibold mt-6 mb-3">Behavior Flags</h3>
    <ConfigTable
      items={[
        { param: "process_only_missing", default: "true", description: "Skip already successful tasks" },
        { param: "skip_successful_tasks", default: "true", description: "Don't reprocess completed work" },
        { param: "bootstrap_mode", default: "false", description: "Quick test mode with limited scope" },
        { param: "dry_run", default: "false", description: "Preview without writing data" },
      ]}
    />

    <h3 className="text-lg font-semibold mt-6 mb-3">Built-in Presets</h3>
    <div className="space-y-2">
      <PresetItem name="Bootstrap (Quick Test)" description="10 funds, 2 quarters - fast initial test" />
      <PresetItem name="Production Incremental" description="100 funds, 4 quarters, skips processed" />
      <PresetItem name="High Quality Long-Term" description="50 funds with 4+ year holding period" />
      <PresetItem name="Broad Universe Discovery" description="500 funds, no performance filters" />
    </div>
  </div>
);

const OperationsSection = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
      <Play className="w-5 h-5 text-primary" />
      Operations Guide
    </h2>

    <h3 className="text-lg font-semibold mt-4 mb-3">Starting a Run</h3>
    <ol className="list-decimal list-inside text-muted-foreground space-y-2">
      <li>Go to Dashboard or Configuration page</li>
      <li>Select a configuration from the dropdown (or create a new one)</li>
      <li>Click "Start" button</li>
      <li>Monitor progress in real-time via Dashboard</li>
    </ol>

    <h3 className="text-lg font-semibold mt-6 mb-3">Pipeline Controls</h3>
    <div className="space-y-2">
      <ControlItem action="Pause" description="Temporarily stops processing. Run can be resumed." />
      <ControlItem action="Resume" description="Continues a paused run from where it stopped." />
      <ControlItem action="Stop" description="Cancels the run. Tasks in progress are marked incomplete." />
    </div>

    <h3 className="text-lg font-semibold mt-6 mb-3">Selective Operations</h3>
    <div className="space-y-2">
      <ControlItem action="Refresh Fund" description="Reset all quarter tasks for a specific CIK to pending" />
      <ControlItem action="Refresh Quarter" description="Reset all fund tasks for a specific year/quarter" />
      <ControlItem action="Retry Failed" description="Reset all failed tasks to pending for re-processing" />
      <ControlItem action="Requeue CUSIPs" description="Queue unresolved CUSIPs for re-resolution" />
    </div>

    <h3 className="text-lg font-semibold mt-6 mb-3">Simulation Mode</h3>
    <p className="text-muted-foreground">
      Before running with new config settings, use "Simulate" to preview:
    </p>
    <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
      <li>How many funds would be selected</li>
      <li>How many tasks would be created</li>
      <li>Estimated API calls</li>
      <li>Affected quarters</li>
    </ul>

    <h3 className="text-lg font-semibold mt-6 mb-3">Email Notifications</h3>
    <p className="text-muted-foreground">
      Email notifications are sent to <code className="text-primary">ez@kensys.io</code> for:
    </p>
    <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
      <li>Pipeline started</li>
      <li>Pipeline completed (with full stats)</li>
      <li>Pipeline failed (with error details)</li>
    </ul>
  </div>
);

const DataModelSection = () => (
  <div className="prose prose-invert max-w-none">
    <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
      <Database className="w-5 h-5 text-primary" />
      Data Model
    </h2>

    <h3 className="text-lg font-semibold mt-4 mb-3">MongoDB Collections</h3>
    
    <div className="space-y-4">
      <CollectionItem
        name="pipeline_configs"
        description="Versioned configuration snapshots"
        fields={["id", "version", "name", "environment", "settings", "created_at"]}
      />
      <CollectionItem
        name="pipeline_runs"
        description="Run history with status and counters"
        fields={["run_id", "status", "trigger_type", "config_id", "started_at", "ended_at", "counters"]}
      />
      <CollectionItem
        name="funds_master"
        description="Fund universe with metadata"
        fields={["cik", "entity_name", "last_filing_date", "portfolio_size", "average_holding_period", "is_active"]}
        index="cik (unique)"
      />
      <CollectionItem
        name="fund_quarter_status"
        description="Per-task tracking (heart of incremental model)"
        fields={["cik", "year", "quarter", "status", "raw_holdings_count", "retries", "last_error"]}
        index="(cik, year, quarter) compound unique"
      />
      <CollectionItem
        name="holdings_enriched"
        description="Final enriched holdings data"
        fields={["cik", "year", "quarter", "holding_cusip_norm", "cusip_symbol", "company_sector", "holding_value"]}
        index="(cik, year, quarter, holding_cusip_norm) compound unique"
      />
      <CollectionItem
        name="cusip_resolution_cache"
        description="CUSIP to symbol mapping cache"
        fields={["holding_cusip_norm", "cusip_symbol", "cusip_company_name"]}
        index="holding_cusip_norm (unique)"
      />
      <CollectionItem
        name="symbol_profile_cache"
        description="Symbol to sector/industry cache"
        fields={["symbol", "company_sector", "company_industry"]}
        index="symbol (unique)"
      />
    </div>
  </div>
);

// Helper Components
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-muted/30 p-4 rounded-sm">
    <Icon className="w-5 h-5 text-primary mb-2" />
    <h4 className="font-medium text-sm">{title}</h4>
    <p className="text-xs text-muted-foreground mt-1">{description}</p>
  </div>
);

const ConceptItem = ({ term, definition }) => (
  <div className="flex gap-3">
    <Badge variant="outline" className="shrink-0 rounded-sm">{term}</Badge>
    <span className="text-muted-foreground text-sm">{definition}</span>
  </div>
);

const ServiceItem = ({ name, description }) => (
  <div className="border-l-2 border-primary/30 pl-3">
    <span className="font-medium text-sm">{name}</span>
    <p className="text-xs text-muted-foreground">{description}</p>
  </div>
);

const StageItem = ({ number, name, description, endpoint }) => (
  <div className="flex gap-4">
    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-primary font-mono text-sm shrink-0">
      {number}
    </div>
    <div>
      <h4 className="font-medium text-sm">{name}</h4>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
      {endpoint && (
        <code className="text-xs text-primary/70 mt-1 block">{endpoint}</code>
      )}
    </div>
  </div>
);

const ConfigTable = ({ items }) => (
  <table className="w-full text-sm">
    <thead>
      <tr className="border-b border-border">
        <th className="text-left py-2 text-muted-foreground font-medium">Parameter</th>
        <th className="text-left py-2 text-muted-foreground font-medium">Default</th>
        <th className="text-left py-2 text-muted-foreground font-medium">Description</th>
      </tr>
    </thead>
    <tbody>
      {items.map((item, i) => (
        <tr key={i} className="border-b border-border/50">
          <td className="py-2 font-mono text-xs text-primary">{item.param}</td>
          <td className="py-2 font-mono text-xs">{item.default}</td>
          <td className="py-2 text-muted-foreground text-xs">{item.description}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const PresetItem = ({ name, description }) => (
  <div className="flex items-center gap-3 bg-muted/30 p-2 rounded-sm">
    <Zap className="w-4 h-4 text-primary" />
    <div>
      <span className="text-sm font-medium">{name}</span>
      <span className="text-xs text-muted-foreground ml-2">{description}</span>
    </div>
  </div>
);

const ControlItem = ({ action, description }) => (
  <div className="flex items-start gap-3">
    <Badge className="bg-primary/20 text-primary border-0 rounded-sm">{action}</Badge>
    <span className="text-sm text-muted-foreground">{description}</span>
  </div>
);

const CollectionItem = ({ name, description, fields, index }) => (
  <div className="bg-muted/30 p-3 rounded-sm">
    <div className="flex items-center gap-2 mb-2">
      <Database className="w-4 h-4 text-primary" />
      <span className="font-mono text-sm text-primary">{name}</span>
    </div>
    <p className="text-xs text-muted-foreground mb-2">{description}</p>
    <div className="flex flex-wrap gap-1">
      {fields.map((f, i) => (
        <Badge key={i} variant="outline" className="text-xs rounded-sm">{f}</Badge>
      ))}
    </div>
    {index && (
      <p className="text-xs text-muted-foreground mt-2">Index: {index}</p>
    )}
  </div>
);

// AI Agent Knowledge Base Section
const AIAgentSection = () => {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchKnowledgeBase();
  }, []);

  const fetchKnowledgeBase = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        setError("Not authenticated");
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API}/api/docs/knowledge-base`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setContent(data.content);
      } else {
        setError(`API error: ${response.status}`);
      }
    } catch (err) {
      console.error("Failed to fetch knowledge base:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "AI_AGENT_KNOWLEDGE_BASE.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  // Simple markdown renderer
  const renderMarkdown = (md) => {
    if (!md) return "";
    
    return md
      .replace(/^### (.*$)/gm, '<h3 class="text-base font-semibold mt-5 mb-2 text-foreground">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="text-lg font-bold mt-6 mb-3 text-foreground border-b border-border pb-2">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="text-xl font-bold mt-4 mb-3 text-foreground">$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-foreground">$1</strong>')
      .replace(/```(\w+)?\n([\s\S]*?)```/g, '<pre class="bg-muted p-3 rounded my-3 overflow-x-auto text-xs font-mono"><code>$2</code></pre>')
      .replace(/`([^`]+)`/g, '<code class="bg-muted px-1 py-0.5 rounded text-xs font-mono text-primary">$1</code>')
      .replace(/^\| /gm, '<tr class="border-b border-border"><td class="px-2 py-1 text-xs">') 
      .replace(/ \| /g, '</td><td class="px-2 py-1 text-xs">')
      .replace(/ \|$/gm, '</td></tr>')
      .replace(/^---$/gm, '<hr class="my-4 border-border" />')
      .replace(/\n\n/g, '</p><p class="my-2 text-sm text-muted-foreground">')
      .replace(/\n/g, '<br />');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary" />
          <h2 className="text-xl font-semibold">AI Agent Knowledge Base</h2>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleCopy}>
            {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
            {copied ? "Copied!" : "Copy"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload}>
            <Download className="h-4 w-4 mr-1" />
            Download
          </Button>
        </div>
      </div>
      
      <p className="text-sm text-muted-foreground">
        Comprehensive documentation for building AI applications on top of the 13F Intelligence Platform.
        This document can be fed to AI agents to help them understand and leverage the nowcasting indicators.
      </p>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="text-red-400 text-center py-8">
          {error}
          <Button variant="outline" size="sm" className="ml-2" onClick={fetchKnowledgeBase}>
            Retry
          </Button>
        </div>
      ) : content ? (
        <div className="bg-muted/30 rounded p-4 border border-border max-h-[600px] overflow-y-auto">
          <pre className="text-sm whitespace-pre-wrap font-mono text-muted-foreground">
            {content}
          </pre>
        </div>
      ) : (
        <div className="text-muted-foreground text-center py-8">
          No content loaded. Please try refreshing.
        </div>
      )}
    </div>
  );
};

// Investor Materials Section
const InvestorMaterialsSection = () => {
  const [downloading, setDownloading] = useState(null);
  const [error, setError] = useState(null);

  const FILE_MAP = {
    std: { url: `${API}/api/docs/pitch-deck?variant=std`, name: "Kensys_Institutional_Presentation.pdf" },
    nb: { url: `${API}/api/docs/pitch-deck?variant=nb`, name: "Kensys_Institutional_Presentation_nb.pdf" },
    millenium: { url: `${API}/api/docs/millenium-deck`, name: "Kensys_Millenium_Presentation.pdf" },
    milleniumv2: { url: `${API}/api/docs/millenium-deck-v2`, name: "Kensys_Millenium_Presentation_v2.pdf" },
    teaser: { url: `${API}/api/docs/teaser`, name: "Kensys_One_Page_Teaser.pdf" },
    mnamc: { url: `${API}/api/docs/mn-amc-deck`, name: "Kensys_US_Market_Neutral_AMC.pdf" },
    mninst: { url: `${API}/api/docs/mn-institutional-deck`, name: "Kensys_US_Market_Neutral_Institutional.pdf" },
    mninst2: { url: `${API}/api/docs/mn-institutional-deck-v2`, name: "Kensys_US_Market_Neutral_Institutional_v2.pdf" },
    mninst3: { url: `${API}/api/docs/mn-institutional-deck-v3`, name: "Kensys_US_Market_Neutral_Institutional_v3.pdf" },
    perp: { url: `${API}/api/docs/perp-research`, name: "Kensys_Equity_Perps_Research_Note.pdf" },
    epp: { url: `${API}/api/docs/earnings-perp-play`, name: "Kensys_Earnings_Perp_Play.pdf" },
    mnmeth: { url: `${API}/api/docs/mn-methodology`, name: "Kensys_MN_Methodology.pdf" },
    bgc: { url: `${API}/api/docs/bgc-deck`, name: "Kensys_BGC_Deck.pdf" },
    bgcpptx: { url: `${API}/api/docs/bgc-deck-pptx`, name: "Kensys_BGC_Deck.pptx" },
    partner: { url: `${API}/api/docs/partner-deck`, name: "Kensys_Partner_Deck.pdf" },
    partnerpptx: { url: `${API}/api/docs/partner-deck-pptx`, name: "Kensys_Partner_Deck.pptx" },
    eapaper: { url: `${API}/api/docs/earnings-alpha-paper`, name: "Kensys_Earnings_Alpha_Whitepaper.pdf" },
    doctrine: { url: `${API}/api/docs/signal-doctrine`, name: "Kensys_Signal_Doctrine.pdf" },
    kfmspec: { url: `${API}/api/docs/kfm-product-spec`, name: "Kensys_Score_Product_Spec.pdf" },
    kfmatlas: { url: `${API}/api/docs/kfm-mockup-atlas`, name: "Kensys_Mockup_Atlas.pdf" },
    extguide: { url: `${API}/api/docs/external-signals-guide`, name: "Kensys_External_Signals_API_Guide.md" },
    extbrief: { url: `${API}/api/docs/external-agent-brief`, name: "Kensys_External_Agent_Brief.md" },
  };

  const handleDownload = async (variant) => {
    setDownloading(variant);
    setError(null);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await fetch(FILE_MAP[variant].url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Generation failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = FILE_MAP[variant].name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setDownloading(null);
    }
  };

  const DeckCard = ({ variant, title, description, testId, pptxVariant, pptxTestId, staticFile }) => (
    <div className="bg-muted/30 rounded-sm p-5 border border-border max-w-xl">
      <h3 className="font-medium text-sm mb-1">{title}</h3>
      <p className="text-xs text-muted-foreground mb-4">{description}</p>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => handleDownload(variant)}
                disabled={downloading !== null} data-testid={testId}>
          {downloading === variant ? (
            <><Loader2 className="h-4 w-4 mr-1 animate-spin" />{staticFile ? "Downloading…" : "Generating with latest data…"}</>
          ) : (
            <><Download className="h-4 w-4 mr-1" />{staticFile ? "Download" : <>Generate &amp; Download</>}</>
          )}
        </Button>
        {pptxVariant && (
          <Button size="sm" variant="outline" onClick={() => handleDownload(pptxVariant)}
                  disabled={downloading !== null} data-testid={pptxTestId}>
            {downloading === pptxVariant ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Generating…</>
            ) : (
              <><Download className="h-4 w-4 mr-1" />PowerPoint</>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="investor-materials-section">
      <div className="flex items-center gap-2">
        <FileText className="w-5 h-5 text-primary" />
        <h2 className="text-xl font-semibold">Investor Materials</h2>
      </div>
      <DeckCard
        variant="mninst3"
        title="Kensys US Market Neutral — Institutional Presentation v3 (PDF)"
        description="v3 of the institutional deck: Market Neutral net of 1% / 10% daily fees as in v2; the UBS Equity Leaders page shows gross of fees with p.a. = total ÷ years (+113.1% vs S&P 500 +83.7%, outperformance +29.4%, i.e. +6.5% p.a.), the Edge page adds a “How we do it” section, and the summary chart has no legend."
        testId="download-mn-institutional-deck-v3-btn"
      />
      <DeckCard
        variant="mninst2"
        title="Kensys US Market Neutral — Institutional Presentation v2 (PDF)"
        description="v2 of the 8-page institutional deck: all Market Neutral figures net of a 1% management fee and a 10% performance fee (high-water mark), both charged daily; summary chart without the S&P 500 and with the monthly net returns table underneath."
        testId="download-mn-institutional-deck-v2-btn"
      />
      <DeckCard
        variant="mninst"
        title="Kensys US Market Neutral — Institutional Presentation (PDF)"
        description="8-page institutional deck for the UBS certificate launch: early allocation (1.00% / 10% on the first USD 20M), daily liquidity with no lock-up, live Equity Leaders UBS AMC outperformance (gross of fees), Market Neutral model performance to the last month end, strategy, terms, team and disclosures."
        testId="download-mn-institutional-deck-btn"
      />
      <DeckCard
        variant="mnamc"
        title="Kensys US Market Neutral AMC — UBS Certificate Deck (PDF)"
        description="4-page investor presentation for the proposed UBS actively managed certificate: the strategy, the current portfolio, simulated whole-share performance at USD 20M net of 1.50% management + 15% performance fee (high-water mark), indicative terms, risks and disclosures."
        testId="download-mn-amc-deck-btn"
      />
      <DeckCard
        variant="teaser"
        title="One-Page Teaser (PDF)"
        description="Single-page email-ready overview — both track records, the edge, the flagship and engagement formats. Built to be skimmed in thirty seconds; regenerated on download with the latest data."
        testId="download-teaser-btn"
      />
      <DeckCard
        variant="std"
        title="Institutional Presentation (PDF)"
        description="5-page allocator deck — the firm, the process, Kensys US Equity Leaders (live UBS AMC) and Kensys Market Neutral. Regenerated on download with the latest stats, live books, sector and factor exposures."
        testId="download-pitch-deck-btn"
      />
      <DeckCard
        variant="nb"
        title="Institutional Presentation — Live Edition (PDF)"
        description="Same deck without any backtest / replay / simulation terminology — performance is presented as live model portfolios since inception (Dec 2023)."
        testId="download-pitch-deck-nb-btn"
      />
      <DeckCard
        variant="millenium"
        title="Millenium Presentation — $150M / 1.0× Gross (PDF)"
        description="5-page allocator deck for the Millenium mandate: Kensys Market Neutral run at $150M with a gross exposure of 1.0× (0.5× long / 0.5× short), full live history since Dec 2023, realized beta only — no ex-ante beta or net-exposure figures. Regenerated on download with the latest stats."
        testId="download-millenium-deck-btn"
      />
      <DeckCard
        variant="milleniumv2"
        title="Millenium Presentation v2 — Alternate Copy (PDF)"
        description="Same $150M / 1.0× gross deck with the v2 wording: hero-headline cover ('The market's best investors tell you what they're doing…') with the flagship stat band, 'Skill is rare' firm page, 'We didn't backtest it. We ran it.' and 'Built to plug into your platform.'"
        testId="download-millenium-deck-v2-btn"
      />
      <DeckCard
        variant="perp"
        title="Research Note — Tokenized Equity Perpetuals (PDF)"
        description="8-page internal study: retail behavior on crypto equity-perp venues, the deployable after-hours earnings momentum edge (execution playbook, stop rules, capacity and money math), the gap oracles, and documented dead ends."
        testId="download-perp-research-btn"
      />
      <DeckCard
        variant="epp"
        title="The Earnings Perp Play — Research Paper (PDF)"
        description="Full paper on the after-hours earnings momentum edge: 42-event trade blotter (+1.16%/event net, t=2.55), why the edge exists, exact playbook, dollar sizing scenarios, risk register and kill criteria."
        testId="download-epp-btn"
      />
      <DeckCard
        variant="doctrine"
        title="Kensys Signal Doctrine — Philosophy & Edge (PDF)"
        description="The definitive internal doctrine: the 45-day blind-spot philosophy, the four counterparties we monetize, the nine validated signal layers (with economic rationale and explicit boundaries), the four live strategies — Entry Anchoring V3 + EA GREEN/RED overlay, Earnings Alpha, Market Neutral v5/v6, Big Cap Champions — the research graveyard and the honesty constitution. Regenerated on download."
        testId="download-signal-doctrine-btn"
      />
      <DeckCard
        variant="kfmspec"
        title="Kensys Score — Product Spec & Factor Definitions (PDF)"
        description="The Smart Money Lens product document: what we are building and for whom (RIA, hedge fund PM/analyst, allocator journeys), the interface mockup, the seven factors in full detail (computation, economic rationale, expanded datapoints, validated evidence AND boundaries), the score/verdict/badge logic, the scientific protocol, admitted limitations and the build roadmap. Regenerated on download."
        testId="download-kfm-spec-btn"
      />
      <DeckCard
        variant="kfmatlas"
        title="Smart Money Lens — Mockup Atlas & Data Dictionary (PDF)"
        description="All 21 /kfm mockups (stock page variants, screener, RIA fund + stock screens, HF analyst, allocator) with persona, pain and how to read each one; a field-by-field data map naming the collection/field behind every number, how to populate it and its readiness (LIVE / RESEARCH / SNAPSHOT / DERIVABLE / SAMPLE / INPUT / EVIDENCE); a 28-concept dictionary; a live data-freshness inventory, build backlog and proposed API surface. Regenerated on download (~30s)."
        testId="download-kfm-atlas-btn"
      />
      <DeckCard
        variant="extguide"
        title="External Signals API — Integration & Interpretation Guide (Markdown)"
        description="Everything an external app or AI agent needs to read the latest Kensys signals with the read-only API key: every /api/ext endpoint (Entry Anchoring V3, live strategies incl. Anchor Long 10 and Mix 5+5, nowcast regimes, champions, L/S µ book, Earnings Alpha GREEN/RED, Rebound Radar, Scouts, crowding, themes, fund grades), field-by-field meaning, how to use each signal, what NOT to conclude, refresh schedule, recipes and agent rules."
        testId="download-ext-guide-btn"
        staticFile
      />
      <DeckCard
        variant="extbrief"
        title="External App Agent — Paste-ready Instructions (Markdown)"
        description="Short system-prompt brief for the external app's AI agent: connection (base URL, X-API-Key, errors), the endpoints it will use most, polling & caching, units, always/never rules, answer pattern for a stock question and a quick connectivity test."
        testId="download-ext-brief-btn"
        staticFile
      />
      <DeckCard
        variant="mnmeth"
        title="Market Neutral — Strategy Methodology (PDF, external)"
        description="External-distribution methodology document for qualified investors: the 13F information edge, evidence-tiered long book, three-family short book, constraint stack, live risk layer and research governance. Pure methodology — no performance data, proprietary parameters described qualitatively."
        testId="download-mn-methodology-btn"
      />
      <DeckCard
        variant="bgc"
        title="BGC Partnership Deck (PDF + PowerPoint)"
        description="9-slide 16:9 discussion document for BGC in the institutional brand (navy/ivory/gold, serif) — cover, intelligence-layer narrative, flywheel, revenue model and scenarios, with the live UBS AMC track record (ISIN CH1182573175) and its full statistics, regenerated on download."
        testId="download-bgc-deck-btn"
        pptxVariant="bgcpptx"
        pptxTestId="download-bgc-deck-pptx-btn"
      />
      <DeckCard
        variant="partner"
        title="Partner Deck — Generic (PDF + PowerPoint)"
        description="Same 9-slide partnership deck with every BGC mention removed — a neutral 'partnership discussion' edition ready to pitch to any brokerage or platform, regenerated on download with the latest AMC data."
        testId="download-partner-deck-btn"
        pptxVariant="partnerpptx"
        pptxTestId="download-partner-deck-pptx-btn"
      />
      <DeckCard
        variant="eapaper"
        title="Earnings Alpha Whitepaper (PDF)"
        description="PhD-style working paper on the anti-crowding earnings signal: pre-registered hypotheses, cluster-robust inference (t=4.96), 162-config selection reality check, placebo battery, capacity analysis and the full graveyard of rejected hypotheses — every number traceable to a verification artifact."
        testId="download-ea-paper-btn"
      />
      {error && <p className="text-red-400 text-xs" data-testid="pitch-deck-error">{error}</p>}
    </div>
  );
};

export default DocumentationPage;
